/**
 * Editor component for managing a module's action workflow.
 * Provides add, edit, delete, and reorder functionality for actions.
 */
import { Plus } from "lucide-react";
import { useState } from "react";

import { ActionConfigDialog, type ModuleActionInfo } from "./ActionConfigDialog";
import { ActionList } from "./ActionList";

import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";


interface ActionEditorProps {
	moduleId: string;
}

/**
 * Editor component for managing workflow actions in a module.
 * @param props - component props
 * @param props.moduleId - the module ID to manage actions for
 */
export function ActionEditor({ moduleId }: ActionEditorProps) {
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingAction, setEditingAction] = useState<ModuleActionInfo | null>(null);

	const utils = trpc.useUtils();

	const actionsQuery = trpc.actions.listModuleActions.useQuery(
		{ moduleId },
		{ enabled: !!moduleId }
	);

	const deleteMutation = trpc.actions.deleteModuleAction.useMutation({
		onSuccess: () => {
			void utils.actions.listModuleActions.invalidate({ moduleId });
		},
	});

	const reorderMutation = trpc.actions.reorderModuleActions.useMutation({
		onSuccess: () => {
			void utils.actions.listModuleActions.invalidate({ moduleId });
		},
	});

	const actions = (actionsQuery.data ?? []) as ModuleActionInfo[];

	/**
	 * Opens the dialog to add a new action
	 */
	const handleAddAction = () => {
		setEditingAction(null);
		setDialogOpen(true);
	};

	/**
	 * Opens the dialog to edit an existing action
	 * @param action - the action to edit
	 */
	const handleEditAction = (action: ModuleActionInfo) => {
		setEditingAction(action);
		setDialogOpen(true);
	};

	/**
	 * Deletes an action after confirmation
	 * @param actionId - the ID of the action to delete
	 */
	const handleDeleteAction = (actionId: string) => {
		if (confirm("Are you sure you want to delete this action?")) {
			deleteMutation.mutate({ actionId });
		}
	};

	/**
	 * Handles action reordering
	 * @param actionOrders - array of action IDs with their new order positions
	 */
	const handleReorder = (actionOrders: { actionId: string; order: number }[]) => {
		reorderMutation.mutate({ moduleId, actionOrders });
	};

	/**
	 * Invalidates the actions query after dialog success
	 */
	const handleDialogSuccess = () => {
		void utils.actions.listModuleActions.invalidate({ moduleId });
	};

	if (actionsQuery.isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h3 className="text-lg font-medium">Workflow Actions</h3>
					<p className="text-sm text-muted-foreground">
						Define the actions users will complete in this module.
					</p>
				</div>
				<Button onClick={handleAddAction}>
					<Plus className="h-4 w-4 mr-2" />
					Add Action
				</Button>
			</div>

			{/* Actions List */}
			<ActionList
				actions={actions}
				onEditAction={handleEditAction}
				onDeleteAction={handleDeleteAction}
				onReorder={handleReorder}
			/>

			{/* Config Dialog */}
			<ActionConfigDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				moduleId={moduleId}
				action={editingAction}
				onSuccess={handleDialogSuccess}
			/>
		</div>
	);
}
